// Zentrale Konstanten. Alles, was Client und Server gemeinsam kennen müssen.
// Wichtig: Diese Datei darf keine Browser-/Node-spezifische Abhängigkeit haben,
// damit beide Seiten exakt dieselben Werte importieren.

import { ACTIVE_SEASON } from './seasons.js';

// v5: Schreibrecht (canWrite), Report, Strich-Herkunftsfeld `at` (§13 / §6.1).
export const PROTOCOL_VERSION = 5;

// --- Weltzeit ---------------------------------------------------------------
// Entscheidung (Konzept §5 / offene Frage 1): beschleunigte Weltzeit.
// 1 Spieltag = 2 Echtzeitstunden  ->  Faktor 12.
export const TIME_SCALE = ACTIVE_SEASON.timeScale;
export const REAL_SECONDS_PER_GAME_DAY = (24 * 3600) / TIME_SCALE; // 7200 s = 2 h
export const GAME_SECONDS_PER_DAY = 24 * 3600;

// Season-Identität kommt aus einem gemeinsamen Deskriptor. So können Server,
// Client, Persistenz und Fotochronik nicht still verschiedene IDs/Seeds nutzen.
export const SEASON_EPOCH_MS = ACTIVE_SEASON.epochMs;
export const SEASON_ID = ACTIVE_SEASON.id;
export const WORLD_SEED = ACTIVE_SEASON.seed;

// --- Netzwerk ---------------------------------------------------------------
// globalThis.process: im Browser ist `process` ungebunden (ReferenceError bei
// `process?.…`), im Node vorhanden. Client und Server teilen diese Datei.
export const SERVER_PORT = Number(globalThis.process?.env?.PORT ?? 8787);
export const SERVER_TICK_HZ = 20; // Snapshots pro Sekunde
export const CLIENT_INPUT_HZ = 20; // Positions-Updates pro Sekunde
export const SNAPSHOT_INTEREST_RADIUS = 900; // m, ab dann keine Remote-Spieler mehr

// --- Spieler ----------------------------------------------------------------
export const PLAYER_HEIGHT = 1.75;
export const PLAYER_EYE_HEIGHT = 1.62;
export const PLAYER_RADIUS = 0.32;
export const WALK_SPEED = 2.7;
export const RUN_SPEED = 6.7; // schnell genug, um einen anfahrenden Zug einzuholen
export const CROUCH_SPEED = 1.2;
export const GRAVITY = 18.0; // etwas härter als 9.81: fühlt sich weniger schwebend an
export const JUMP_SPEED = 5.2;
export const CLIMB_SPEED = 1.7;
export const MAX_STEP_HEIGHT = 0.45;

// Aufspringen: bis zu welcher Relativgeschwindigkeit ein Griff hält.
export const GRAB_MAX_RELATIVE_SPEED = 3.4; // m/s
// GRAB_REACH: Gesamt-3D-Reserve / Kompatibilität (Kollisionstests, alte Pfade).
// Die echte Reichweite trennt horizontal und vertikal — siehe Handhöhe in climb.js.
// Begründung: Griff liegt ~2,3 m über den Füßen; Distanz Füße→Griff frisst 1,6 m auf.
export const GRAB_REACH = 1.6; // m
export const GRAB_HORIZ = 1.05; // m, Körper→Leitersäule (XZ)
export const GRAB_VERT_UP = 1.40; // m über der Hand (über Kopf greifbar)
export const GRAB_VERT_DOWN = 0.90; // m unter der Hand (Knie/Hüfte)
export const HAND_HEIGHT_STAND = 1.30; // m über Füßen
export const HAND_HEIGHT_CROUCH = 0.90;
// Halten von E: Physik erzeugt die Hürde (mitlaufen), nicht nur die Speed-Zahl.
// 0,35 s: stehender Zug trivial; Vorbeifliegen mit rel>2 m/s verlässt die Zone
// bevor der Fortschritt voll ist (~0,5 m Tiefe / 2 m/s ≈ 0,25 s — knapp).
export const GRAB_HOLD_TIME = 0.35; // s
export const GRAB_HOLD_DECAY = 2.6; // Verfall relativ zur Füllrate
// Mantling (Kante hochdrücken). Niedrige Kanten → MAX_STEP_HEIGHT (Laufen).
export const MANTLE_MIN_HEIGHT = 0.50; // m über Füßen: darunter Step, darüber Mantle
export const MANTLE_MAX_HEIGHT = 1.90; // m: darüber unerreichbar ohne Leiter
export const MANTLE_DURATION = 0.45; // s unbeweglich beim Hochziehen
export const MANTLE_FORWARD = 0.55; // m Suchdistanz vor dem Spieler
export const FALL_HURT_SPEED = 9.0;

// --- Graffiti ---------------------------------------------------------------
// "einfache Grafitti funktion ohne resource drain": eine Canvas-Textur pro
// bemalbarer Fläche, Striche als Polylinien, kein Per-Frame-Rendering.
export const PAINT_SURFACE_PX_PER_M = 64; // Auflösung der Malfläche
export const PAINT_MAX_TEXTURE = 2048;
export const PAINT_MIN_POINT_DISTANCE = 0.012; // m, Punkt-Dezimierung beim Malen
export const PAINT_MAX_POINTS_PER_STROKE = 400;
export const PAINT_MAX_STROKES_PER_SURFACE = 4000; // hartes Dach gegen Endlos-Wachstum
export const PAINT_REACH = 2.2; // m
export const PAINT_SPRAY_RATE = 1.0; // Farbeinheiten pro Sekunde Sprühen
export const PAINT_CAN_CAPACITY = 26.0; // ~26 s Dauersprühen pro Dose

export const PAINT_COLORS = [
  '#f2f0e6', '#101014', '#c8352a', '#e07b1f', '#f2c53d',
  '#4f9d3a', '#2e6fb7', '#7a4fa3', '#d95d9a', '#8a6a45',
];

// --- Funk -------------------------------------------------------------------
export const RADIO_MIN_CHANNEL = 100; // "1,00"
export const RADIO_MAX_CHANNEL = 599; // "5,99"  -> 500 Kanäle (Konzept §9: klein halten)
export const RADIO_BASE_RANGE = 620; // m auf Bodenhöhe, freie Sicht
export const RADIO_HEIGHT_BONUS = 34; // m Reichweite pro m Höhe über Gelände
export const RADIO_MAX_RANGE = 4200;
export const RADIO_MESSAGE_MAX_LEN = 160;
export const RADIO_HISTORY = 60;

// --- Zustand (Kälte/Nässe, Konzept §11.1) -----------------------------------
export const WARMTH_MAX = 100;
export const WETNESS_MAX = 100;

// --- Welt -------------------------------------------------------------------
export const RAIL_GAUGE = 1.435;
export const RAIL_HEAD_WIDTH = 0.07;
export const SLEEPER_SPACING = 0.62;
export const TRACK_BED_HALF_WIDTH = 2.6;
export const WORLD_UP = [0, 1, 0];

// Wie weit vom Gleis entfernt Detail existiert (Konzept §12.1: ein Korridor,
// keine Welt).
export const CORRIDOR_HALF_WIDTH = 320;
export const TERRAIN_TILE_SIZE = 64;
export const TERRAIN_VIEW_TILES = 9;

export const DEBUG = true;
